import {
  BarChart3,
  Boxes,
  ClipboardList,
  LockKeyhole,
  LogOut,
  Map,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  X,
} from '../lib/icons';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../features/auth/AuthContext';
import { IdleLockOverlay } from '../features/auth/IdleLockOverlay';
import type { AppRole } from '../features/auth/types';
import { GlobalProductSearch } from '../features/scanner/GlobalProductSearch';
import {
  OPERATIONAL_UNIT_LABELS,
  type OperationalUnitCode,
} from '../features/units/types';

const skofjaLokaNavigation = [
  {
    to: '/izdelki',
    label: 'Izdelki',
    icon: Boxes,
    roles: ['ADMIN', 'SKLADISCNIK', 'PAKER'],
  },
  { to: '/mapa', label: 'Mapa', icon: Map, roles: ['ADMIN', 'SKLADISCNIK'] },
  {
    to: '/narocila',
    label: 'Naročila',
    icon: ClipboardList,
    roles: ['ADMIN', 'SKLADISCNIK', 'PAKER'],
  },
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    roles: ['ADMIN', 'SKLADISCNIK'],
  },
  { to: '/nastavitve', label: 'Nastavitve', icon: Settings, roles: ['ADMIN'] },
] satisfies Array<{
  to: string;
  label: string;
  icon: typeof Boxes;
  roles: AppRole[];
}>;

const logatecNavigation = [
  {
    to: '/logatec/izdelki',
    label: 'Izdelki',
    icon: Boxes,
    roles: ['ADMIN', 'SKLADISCNIK', 'PAKER'],
  },
  {
    to: '/logatec/mapa',
    label: 'Mapa',
    icon: Map,
    roles: ['ADMIN', 'SKLADISCNIK', 'PAKER'],
  },
  {
    to: '/logatec/nastavitve',
    label: 'Nastavitve',
    icon: Settings,
    roles: ['ADMIN'],
  },
] satisfies Array<{
  to: string;
  label: string;
  icon: typeof Boxes;
  roles: AppRole[];
}>;

const roleLabels: Record<AppRole, string> = {
  ADMIN: 'Administrator',
  SKLADISCNIK: 'Skladiščnik',
  PAKER: 'Paker',
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'wms-sidebar-collapsed';

export function AppLayout({ unitCode }: { unitCode: OperationalUnitCode }) {
  const { user, lock, locked, signOut } = useAuth();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );
  const sidebarRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navigation =
    unitCode === 'SKOFJA_LOKA' ? skofjaLokaNavigation : logatecNavigation;
  const visibleNavigation = navigation.filter(
    ({ roles }) => user && roles.includes(user.role),
  );
  const canCreateProducts =
    user?.role === 'ADMIN' || user?.role === 'SKLADISCNIK';
  const canSwitchUnit = (user?.unitCodes.length ?? 0) > 1;

  useEffect(() => {
    if (!navigationOpen) return;
    sidebarRef.current?.querySelector<HTMLElement>('button, a')?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setNavigationOpen(false);
      menuButtonRef.current?.focus();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [navigationOpen]);

  const closeNavigation = () => setNavigationOpen(false);
  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      const nextCollapsed = !collapsed;
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_STORAGE_KEY,
        String(nextCollapsed),
      );
      return nextCollapsed;
    });
  };

  return (
    <div className={`app-shell${sidebarCollapsed ? ' is-collapsed' : ''}`}>
      <aside
        className={`app-sidebar${navigationOpen ? ' is-open' : ''}`}
        aria-label="Glavna navigacija"
        aria-hidden={locked}
        id="app-navigation"
        ref={sidebarRef}
      >
        <button
          className="icon-button app-sidebar__collapse"
          type="button"
          aria-label={
            sidebarCollapsed ? 'Razširi navigacijo' : 'Skrči navigacijo'
          }
          aria-controls="app-navigation"
          aria-expanded={!sidebarCollapsed}
          onClick={toggleSidebar}
        >
          {sidebarCollapsed ? (
            <PanelLeftOpen size={18} />
          ) : (
            <PanelLeftClose size={18} />
          )}
        </button>
        <div className="app-sidebar__header">
          <div className="app-brand">
            <img
              className="app-brand__logo"
              src="/Logo_WMS.png"
              alt=""
              width="40"
              height="41"
            />
            <span className="app-brand__text">
              <strong>WMS</strong>
              <small>{OPERATIONAL_UNIT_LABELS[unitCode]}</small>
            </span>
          </div>
          <button
            className="icon-button app-sidebar__close"
            type="button"
            aria-label="Zapri navigacijo"
            onClick={closeNavigation}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="app-nav">
          {visibleNavigation.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `app-nav__link${isActive ? ' is-active' : ''}`
              }
              onClick={closeNavigation}
              title={sidebarCollapsed ? label : undefined}
            >
              <Icon size={20} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <button
        className={`app-sidebar-backdrop${navigationOpen ? ' is-open' : ''}`}
        type="button"
        aria-label="Zapri navigacijo s klikom zunaj"
        tabIndex={navigationOpen ? 0 : -1}
        onClick={closeNavigation}
      />
      <main className="app-main" aria-hidden={locked}>
        <header className="app-header">
          <div className="header-leading">
            <button
              className="header-button app-menu-button"
              type="button"
              aria-label="Odpri navigacijo"
              aria-controls="app-navigation"
              aria-expanded={navigationOpen}
              ref={menuButtonRef}
              onClick={() => setNavigationOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="header-user">
              <strong>{user?.displayName}</strong>
              <span>{user ? roleLabels[user.role] : ''}</span>
            </div>
          </div>
          {unitCode === 'SKOFJA_LOKA' ? (
            <GlobalProductSearch canCreate={canCreateProducts} />
          ) : (
            <div aria-hidden="true" />
          )}
          <div className="header-actions">
            {canSwitchUnit && (
              <Link className="header-button" to="/enote">
                <Boxes size={18} />
                <span>Enote</span>
              </Link>
            )}
            <button className="header-button" type="button" onClick={lock}>
              <LockKeyhole size={18} />
              <span>Zakleni</span>
            </button>
            <button
              className="header-button"
              type="button"
              onClick={() => void signOut()}
            >
              <LogOut size={18} />
              <span>Odjava</span>
            </button>
          </div>
        </header>
        <div className="app-content">
          <Outlet />
        </div>
      </main>
      <IdleLockOverlay />
    </div>
  );
}
